/**
 * The sample flight record: today's movements from the sample flight day,
 * a thirty-day history for the predictor to learn from, the seasonal lines
 * the day comes from, and a message inbox.
 *
 * DEMONSTRATION records. The history is generated — each service has its own
 * habit (SV1483 runs about twenty minutes late, the early-morning flights are
 * on time), deterministic so the predictions read the same every time — and
 * the screen says the record is a sample.
 */
import { mockFlights } from "./flights";
import type { Flight } from "../domain/flights";
import type { Movement, MovementMessage, Past, ScheduleLine } from "../domain/aodb";

const hash = (s: string) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
const localDate = (iso: string) => new Date(new Date(iso).getTime() + 3 * 3_600_000).toISOString().slice(0, 10);

/** Each service's habit, in minutes late — the thing the predictor should find. */
export function habitOf(carrier: string, number: string): number {
  if (`${carrier}${number}` === "SV1483") return 22;
  if (`${carrier}${number}` === "SV1478") return 12;
  const h = hash(`${carrier}${number}`);
  return (h % 5 === 0 ? 18 : h % 3 === 0 ? 8 : 2) + (h % 4);
}

export function mockHistory(flights: readonly Flight[], days = 30): Past[] {
  const out: Past[] = [];
  const seen = new Set<string>();
  for (const f of flights) {
    if (!f.carrier || !f.number || !f.scheduledAt || f.source === "forecast") continue;
    const key = `${f.carrier}${f.number}${f.direction}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const habit = habitOf(f.carrier, f.number);
    for (let k = 1; k <= days; k++) {
      const s = new Date(new Date(f.scheduledAt).getTime() - k * 86_400_000);
      const jitter = ((hash(`${key}${k}`) % 9) - 4) * 1.0;
      out.push({
        carrier: f.carrier,
        number: f.number,
        direction: f.direction,
        scheduledAt: s.toISOString(),
        actualAt: new Date(s.getTime() + (habit + jitter) * 60_000).toISOString(),
      });
    }
  }
  return out;
}

/** How each operator's estimates have turned out: SV's run eight minutes early. */
export function biasOf(carrier: string): { minutes: number | null; n: number } {
  if (carrier === "SV") return { minutes: 8, n: 64 };
  if (carrier === "XY") return { minutes: 3, n: 21 };
  return { minutes: null, n: 2 };
}

export function turnOf(carrier: string): { minutes: number; n: number } {
  return carrier === "SV" ? { minutes: 40, n: 58 } : carrier === "XY" ? { minutes: 35, n: 22 } : { minutes: 35, n: 0 };
}

export function mockMovements(flights: readonly Flight[] = mockFlights()): Movement[] {
  const byTurn = new Map<string, Flight[]>();
  for (const f of flights) if (f.turnaround) byTurn.set(f.turnaround, [...(byTurn.get(f.turnaround) ?? []), f]);
  return flights
    .filter((f) => f.carrier && f.number && f.scheduledAt && f.source !== "forecast")
    .map((f) => {
      const pair = f.turnaround ? byTurn.get(f.turnaround)?.find((x) => x.id !== f.id) : undefined;
      const reg = f.turnaround
        ? `HZ-${String.fromCharCode(65 + (hash(f.turnaround) % 26))}${String.fromCharCode(65 + (hash(f.turnaround + "x") % 26))}${String.fromCharCode(65 + (hash(f.turnaround + "y") % 26))}`
        : undefined;
      const status: Movement["status"] = f.cancelled
        ? "cancelled"
        : f.actualAt
          ? f.direction === "departure"
            ? "departed"
            : "arrived"
          : f.estimatedAt &&
              f.scheduledAt &&
              new Date(f.estimatedAt).getTime() - new Date(f.scheduledAt).getTime() > 15 * 60_000
            ? "delayed"
            : "scheduled";
      return {
        id: f.id,
        direction: f.direction,
        carrier: f.carrier!,
        number: f.number!,
        registration: reg,
        classKey: f.aircraftIcao,
        counterpartNode: f.otherAirport,
        serviceType: f.serviceType,
        serviceDate: localDate(f.scheduledAt!),
        status,
        scheduledAt: f.scheduledAt,
        estimatedAt: f.estimatedAt,
        actualAt: f.actualAt,
        milestones: (f.actualAt
          ? f.direction === "departure"
            ? { left_position: f.actualAt }
            : { reached_position: f.actualAt }
          : {}) as Record<string, string>,
        handling: {},
        loads: f.passengers ? { booked: f.passengers, ...(f.actualAt ? { actual: f.passengers } : {}) } : {},
        capacity: f.seats,
        delayCodes: f.delayCode ? [{ code: f.delayCode, source: "entered" }] : [],
        linkedMovementId: pair?.id,
        remarks: f.delayNote,
        cancelledReason: f.cancelled?.reason,
        /* the delay stories: who told us, when, and the time they gave */
        notices: (f.notices ?? []).map((n) => ({
          at: n.at,
          source: n.source,
          by: n.by,
          newTime: n.newTime,
          code: n.code,
          note: n.note,
        })),
        flags: [],
        version: 1,
        source: f.source,
      };
    });
}

export function mockSchedule(moves: readonly Movement[]): ScheduleLine[] {
  const today = new Date();
  const from = new Date(today.getTime() - 20 * 86_400_000).toISOString().slice(0, 10);
  const to = new Date(today.getTime() + 150 * 86_400_000).toISOString().slice(0, 10);
  return moves
    .filter((m) => m.status !== "cancelled")
    .map((m, i) => ({
      id: `line-${i + 1}`,
      season: "W26",
      carrier: m.carrier,
      number: m.number,
      direction: m.direction,
      validFrom: from,
      validTo: to,
      days: hash(m.carrier + m.number) % 3 === 0 ? [1, 3, 5, 7] : [1, 2, 3, 4, 5, 6, 7],
      localTime: new Date(new Date(m.scheduledAt!).getTime() + 3 * 3_600_000).toISOString().slice(11, 16),
      counterpartNode: m.counterpartNode,
      classKey: m.classKey,
      serviceType: m.serviceType,
      capacity: m.capacity,
      source: "ssim",
    }));
}

export function mockInbox(moves: readonly Movement[]): MovementMessage[] {
  return moves
    .filter((m) => m.actualAt)
    .slice(0, 6)
    .map((m, i) => {
      const t = new Date(m.actualAt!).toISOString().slice(11, 16).replace(":", "");
      const raw =
        m.direction === "departure"
          ? `MVT\n${m.carrier}${m.number}/${m.serviceDate.slice(8, 10)}.${(m.registration ?? "HZXXX").replace("-", "")}.MED\nAD${t}/${t}`
          : `MVT\n${m.carrier}${m.number}/${m.serviceDate.slice(8, 10)}.${(m.registration ?? "HZXXX").replace("-", "")}.MED\nAA${t}/${t}`;
      return {
        id: `msg-${i + 1}`,
        kind: "mvt",
        raw,
        receivedAt: m.actualAt!,
        outcome: "applied" as const,
        changes: ["actual_at", "status", "milestones"],
        movement: `${m.carrier}${m.number} ${m.direction}`,
        movementId: m.id,
      };
    });
}

/* ── the rolling wave ─────────────────────────────────────────────────── */

type Fleet = { icao: string; size: "C" | "D" | "E"; seats: number };
const NARROW: Fleet = { icao: "A20N", size: "C", seats: 186 };
const FLEETS: Record<string, Fleet[]> = {
  SV: [NARROW, NARROW, { icao: "A321", size: "C", seats: 196 }, { icao: "A321", size: "C", seats: 196 }, { icao: "A333", size: "E", seats: 329 }, { icao: "B77W", size: "E", seats: 410 }],
  XY: [NARROW, NARROW, { icao: "A321", size: "C", seats: 212 }],
  F3: [NARROW],
  MS: [{ icao: "B738", size: "C", seats: 154 }, { icao: "A333", size: "E", seats: 301 }],
  PK: [{ icao: "A320", size: "C", seats: 168 }, { icao: "B77W", size: "E", seats: 365 }],
  TK: [{ icao: "A321", size: "C", seats: 180 }, { icao: "A333", size: "E", seats: 289 }],
  GA: [{ icao: "A333", size: "E", seats: 360 }, { icao: "B77W", size: "E", seats: 393 }],
  EK: [{ icao: "B77W", size: "E", seats: 354 }],
  J9: [NARROW],
  FZ: [{ icao: "B38M", size: "C", seats: 174 }],
  G9: [{ icao: "A320", size: "C", seats: 174 }],
  ET: [{ icao: "B763", size: "D", seats: 245 }],
};
const CARRIERS = ["SV", "SV", "SV", "SV", "SV", "XY", "XY", "XY", "XY", "F3", "F3", "MS", "PK", "TK", "GA", "EK", "J9", "FZ", "G9", "ET"];
const HOMES: Record<string, string[]> = {
  SV: ["RUH", "JED", "DMM", "CAI", "KHI", "CGK"],
  XY: ["RUH", "DMM", "AHB", "TUU", "GIZ"],
  F3: ["RUH", "DMM", "TIF"],
  MS: ["CAI"], PK: ["KHI", "LHE", "ISB"], TK: ["IST"], GA: ["CGK", "SUB"], EK: ["DXB"],
  J9: ["KWI"], FZ: ["DXB"], G9: ["SHJ"], ET: ["ADD"],
};

/**
 * The next hours of traffic, generated from the seasonal pattern and
 * anchored to the clock — so the record holds a real airport's rolling day at
 * any hour anybody opens it, rather than a fixed day that has flown by the
 * evening. Deterministic per half-hour: reload and the same flights are there.
 *
 * Madinah's shape: a morning and an evening bank, and a share of pilgrim
 * widebodies competing for the two code-E contact stands.
 */
export function mockWave(now: Date, back = 3, ahead = 14): Flight[] {
  const SLOT = 30 * 60_000;
  const out: Flight[] = [];
  const first = Math.floor((now.getTime() - back * 3_600_000) / SLOT);
  const last = Math.floor((now.getTime() + ahead * 3_600_000) / SLOT);
  const used = new Set<string>();
  for (let idx = first; idx <= last; idx++) {
    const hour = Math.floor(((idx * SLOT) / 3_600_000 + 3) % 24);
    /* arrivals per half hour: a morning and an evening bank */
    const rate = hour < 5 ? 0 : hour < 8 ? 1 : hour < 12 ? 2 : hour < 16 ? 1 : hour < 22 ? 2 : 1;
    const n = Math.max(0, rate - (hash(`slot${idx}`) % 3 === 0 ? 1 : 0));
    for (let k = 0; k < n; k++) {
      const h = hash(`${idx}:${k}`);
      const carrier = CARRIERS[h % CARRIERS.length];
      const fleet = FLEETS[carrier];
      const type = fleet[(h >>> 4) % fleet.length];
      let num = 2000 + ((h >>> 8) % 450) * 2;
      while (used.has(`${carrier}${num}`)) num += 2;
      used.add(`${carrier}${num}`);
      const arr = idx * SLOT + ((h >>> 3) % 6) * 5 * 60_000;
      const ground = type.size === "E" ? 100 + ((h >>> 5) % 5) * 10 : type.size === "D" ? 85 : 50 + ((h >>> 5) % 5) * 5;
      const dep = arr + ground * 60_000;
      const other = HOMES[carrier][(h >>> 6) % HOMES[carrier].length];
      const pax = Math.round(type.seats * (0.72 + ((h >>> 7) % 25) / 100));
      const turn = `wv-${idx}-${k}`;
      const lateArr = habitOf(carrier, String(num)) + (((h >>> 9) % 7) - 3);
      const lateDep = habitOf(carrier, String(num + 1)) + (((h >>> 10) % 7) - 3);
      const arrActual = arr + lateArr * 60_000;
      const depActual = Math.max(dep + lateDep * 60_000, arrActual + (ground - 15) * 60_000);
      const soon = arr > now.getTime() && arr - now.getTime() < 3 * 3_600_000 && h % 3 === 0;
      const base = { carrier, aircraftIcao: type.icao, size: type.size, turnaround: turn, serviceType: "pax" as const, source: "schedule" as const, seats: type.seats };
      out.push({
        ...base,
        id: `${turn}-a`,
        direction: "arrival",
        number: String(num),
        otherAirport: other,
        scheduledAt: new Date(arr).toISOString(),
        estimatedAt: soon ? new Date(arr + (lateArr + 4) * 60_000).toISOString() : undefined,
        actualAt: arrActual < now.getTime() ? new Date(arrActual).toISOString() : undefined,
        passengers: pax,
      });
      out.push({
        ...base,
        id: `${turn}-d`,
        direction: "departure",
        number: String(num + 1),
        otherAirport: other,
        scheduledAt: new Date(dep).toISOString(),
        actualAt: depActual < now.getTime() ? new Date(depActual).toISOString() : undefined,
        passengers: Math.round(pax * 0.97),
      });
    }
  }
  return out;
}
